import { supabase } from '../../config/supabase';

/**
 * Get all drivers for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of drivers
 */
export const getCompanyDrivers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email, phone, role, status')
            .eq('company_id', companyId)
            .eq('role', 'driver')
            .eq('status', 'active')
            .order('username', { ascending: true });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error fetching company drivers:', error);
        throw error;
    }
};

/**
 * Assign a driver to a delivery
 * @param {string} deliveryId - The job_deliveries ID
 * @param {string} driverId - The driver's user ID
 * @param {string} driverName - The driver's name
 * @param {string} assignedBy - The user who assigned the driver
 * @returns {Promise<Object>} Updated delivery
 */
export const assignDriverToDelivery = async (deliveryId, driverId, driverName, assignedByUserId, assignedByName, quoteId = null, quoteUnique = null) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .update({
                assigned_driver_id: driverId,
                assigned_driver_name: driverName,
                assigned_at: new Date().toISOString(),
                assigned_by_user_id: assignedByUserId,
                assigned_by_name: assignedByName,
                delivery_status: 'scheduled', // Update status when driver is assigned
            })
            .eq('id', deliveryId)
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error assigning driver to delivery:', error);
        throw error;
    }
};


/**
 * Remove driver from a delivery
 * @param {string} deliveryId - The job_deliveries ID
 * @returns {Promise<Object>} Updated delivery
 */
export const removeDriverFromDelivery = async (deliveryId) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .update({
                assigned_driver_id: null,
                assigned_driver_name: null,
                assigned_at: null,
                assigned_by_user_id: null,
                assigned_by_name: null,
                delivery_status: 'not_scheduled' // Reset status when driver is removed
            })
            .eq('id', deliveryId)
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error removing driver from delivery:', error);
        throw error;
    }
};

const getAustralianDate = () => {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
};

const toDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * The current month's workload window: 1st of the month through the last
 * Friday of the month (deliveries don't run on weekends).
 * @returns {{ start: string, end: string }} YYYY-MM-DD bounds, inclusive
 */
const getCurrentMonthWorkloadWindow = () => {
    const now = getAustralianDate();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);

    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const dayOfWeek = lastDayOfMonth.getDay(); // 0 = Sun ... 5 = Fri, 6 = Sat
    const daysSinceFriday = (dayOfWeek + 2) % 7;
    const lastFriday = new Date(lastDayOfMonth);
    lastFriday.setDate(lastFriday.getDate() - daysSinceFriday);

    return { start: toDateString(start), end: toDateString(lastFriday) };
};

/**
 * Get driver workload — count of assigned deliveries scheduled within the
 * current month, from the 1st through the last Friday of the month.
 *
 * NOTE: job_deliveries.delivery_date is only set once, at row creation, and
 * is never updated when a delivery is rescheduled — dragging a card to a new
 * day only updates job_quotes.expected_delivery_date. So the live scheduled
 * date has to be read from job_quotes, matched the same way the rest of the
 * schedule UI matches a delivery to its quote (by child_id for new-system
 * quotes, or by job_id + quote_type + delivery_count for old-system ones).
 *
 * @param {string} driverId - The driver's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<number>} Count of assigned deliveries this month
 */
export const getDriverWorkload = async (driverId, companyId) => {
    try {
        const { start, end } = getCurrentMonthWorkloadWindow();

        const { data: deliveries, error } = await supabase
            .from('job_deliveries')
            .select('id, job_id, quote_type, delivery_count, child_id')
            .eq('assigned_driver_id', driverId)
            .neq('delivery_status', 'delivered')
            .neq('delivery_status', 'cancelled');

        if (error) throw error;
        if (!deliveries || deliveries.length === 0) return 0;

        const jobIds = [...new Set(deliveries.map(d => d.job_id))];

        const { data: quotes, error: quotesError } = await supabase
            .from('job_quotes')
            .select('id, job_id, quote_type, delivery_count, expected_delivery_date')
            .in('job_id', jobIds);

        if (quotesError) throw quotesError;

        return deliveries.filter(delivery => {
            const matchedQuote = delivery.child_id
                ? quotes.find(q => q.id === delivery.child_id)
                : quotes.find(q =>
                    q.job_id === delivery.job_id &&
                    q.quote_type === delivery.quote_type &&
                    (q.delivery_count ?? 0) === (delivery.delivery_count ?? 0)
                );

            const scheduledDate = matchedQuote?.expected_delivery_date;
            if (!scheduledDate) return false;

            const dateStr = scheduledDate.slice(0, 10);
            return dateStr >= start && dateStr <= end;
        }).length;
    } catch (error) {
        console.error('Error getting driver workload:', error);
        return 0;
    }
};

/**
 * Get driver's assigned deliveries with job details
 * @param {string} driverId - The driver's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of deliveries with job info
 */
export const getDriverDeliveries = async (driverId, companyId) => {
    try {
        const { data, error } = await supabase
            .from('job_deliveries')
            .select(`
                *,
                jobs!inner (
                    id,
                    unique_id,
                    customer_name,
                    customer_address,
                    company_id
                )
            `)
            .eq('assigned_driver_id', driverId)
            .eq('jobs.company_id', companyId)
            .neq('delivery_status', 'delivered')
            .neq('delivery_status', 'cancelled')
            .order('delivery_date', { ascending: true, nullsFirst: false });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error getting driver deliveries:', error);
        throw error;
    }
};

/**
 * Subscribe to driver assignment changes
 * @param {string} companyId - Company ID
 * @param {Function} callback - Callback function when changes occur
 * @returns {RealtimeChannel} Supabase channel
 */
export const subscribeToDriverAssignments = (companyId, callback) => {
    const channel = supabase
        .channel(`driver-assignments-${companyId}-${Date.now()}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_deliveries',
            },
            (payload) => {
                callback(payload);
            }
        )
        .subscribe();

    return channel;
};
